<template>
  <div class="page-container">
    <div class="toolbar">
      <div class="actions">
        <el-button type="primary" @click="openModal">新建工单</el-button>
      </div>
      <div class="filters">
        <el-input v-model="query.orderNo" placeholder="工单号" clearable style="width:160px" @keyup.enter="loadWorkOrders" />
        <el-input v-model="query.title" placeholder="标题" clearable style="width:160px" @keyup.enter="loadWorkOrders" />
        <el-select v-model="query.status" placeholder="状态" clearable style="width:120px" @change="loadWorkOrders">
          <el-option label="待处理" :value="0" />
          <el-option label="处理中" :value="1" />
          <el-option label="已完成" :value="2" />
        </el-select>
        <el-button type="primary" @click="loadWorkOrders">查询</el-button>
        <el-button @click="resetQuery">重置</el-button>
      </div>
    </div>

    <el-table stripe :data="filteredWorkOrders" border>
      <el-table-column prop="orderNo" label="工单号" min-width="130" />
      <el-table-column prop="title" label="工单标题" min-width="140" show-overflow-tooltip />
      <el-table-column prop="content" label="工单内容" min-width="180" show-overflow-tooltip />
      <el-table-column prop="priority" label="优先级" width="80" align="center">
        <template #default="scope">
          <el-tag :type="getPriorityType(scope.row.priority)" size="small">{{ getPriorityName(scope.row.priority) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="90" align="center">
        <template #default="scope">
          <el-tag :type="getStatusType(scope.row.status)" size="small">{{ getStatusName(scope.row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="所属粮库" min-width="120">
        <template #default="scope">{{ scope.row.grainStoreName || '-' }}</template>
      </el-table-column>
      <el-table-column label="上报人" width="100">
        <template #default="scope">{{ scope.row.reporterName || scope.row.reporter || '-' }}</template>
      </el-table-column>
      <el-table-column label="接收人" width="100">
        <template #default="scope">{{ scope.row.receiverName || scope.row.receiver || '-' }}</template>
      </el-table-column>
      <el-table-column label="处理人" width="100">
        <template #default="scope">{{ scope.row.assigneeName || scope.row.assignee || '-' }}</template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" min-width="160">
        <template #default="scope">{{ formatDateTime(scope.row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="240" fixed="right">
        <template #default="scope">
          <el-button link type="primary" size="small" @click="viewDetail(scope.row)">详情</el-button>
          <el-button link type="warning" size="small" @click="openAssign(scope.row)" v-if="scope.row.status !== 2">派单</el-button>
          <el-button link type="danger" size="small" @click="handleDeleteWorkOrder(scope.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <MyPager :total="total" v-model:page="pageNum" v-model:size="pageSize" @change="loadWorkOrders" />

    <!-- 新建/编辑工单弹窗 -->
    <el-dialog title="工单信息" v-model="dialogVisible" width="600px">
      <el-form :model="formData" label-width="100px">
        <el-form-item label="工单标题">
          <el-input v-model="formData.title" />
        </el-form-item>
        <el-form-item label="工单内容">
          <el-input v-model="formData.content" type="textarea" :rows="3" />
        </el-form-item>
        <el-form-item label="所属粮库">
          <el-select v-model="formData.grainStoreId" placeholder="请选择粮库">
            <el-option v-for="s in grainStores" :key="s.id" :label="s.storeName" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="上报人">
          <el-select v-model="formData.reporterId" placeholder="选择系统用户" filterable>
            <el-option v-for="u in users" :key="u.id" :label="u.realName" :value="u.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveWorkOrder">确定</el-button>
      </template>
    </el-dialog>

    <!-- 派单弹窗（设置处理人 + 优先级） -->
    <el-dialog title="派单" v-model="assignVisible" width="500px">
      <el-form :model="assignForm" label-width="100px">
        <el-form-item label="工单">
          <span>{{ assignRow?.orderNo }} · {{ assignRow?.title }}</span>
        </el-form-item>
        <el-form-item label="优先级">
          <el-select v-model="assignForm.priority">
            <el-option label="紧急" :value="1" />
            <el-option label="普通" :value="2" />
            <el-option label="低" :value="3" />
          </el-select>
        </el-form-item>
        <el-form-item label="处理人">
          <el-select v-model="assignForm.assigneeId" placeholder="选择处理人" filterable>
            <el-option v-for="u in users" :key="u.id" :label="u.realName + (u.grainStoreName ? '（' + u.grainStoreName + '）' : '')" :value="u.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="assignVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAssign">确定派单</el-button>
      </template>
    </el-dialog>

    <!-- 工单详情 overlay -->
    <transition name="manage-fade">
      <div v-if="detailVisible" class="order-mask" @click.self="detailVisible = false">
        <div class="order-panel">
          <div class="order-title">
            <span>工单详情 · {{ detail?.orderNo || '' }}</span>
            <el-button text circle @click="detailVisible = false">
              <el-icon :size="18"><Close /></el-icon>
            </el-button>
          </div>
          <div class="order-body">
            <template v-if="detail">
              <el-descriptions :column="2" border>
                <el-descriptions-item label="工单标题" :span="2">{{ detail.title }}</el-descriptions-item>
                <el-descriptions-item label="状态">
                  <el-tag :type="getStatusType(detail.status)">{{ getStatusName(detail.status) }}</el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="优先级">
                  <el-tag :type="getPriorityType(detail.priority)" size="small">{{ getPriorityName(detail.priority) }}</el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="所属粮库">{{ detail.grainStoreName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="创建时间">{{ formatDateTime(detail.createTime) }}</el-descriptions-item>
                <el-descriptions-item label="上报人">{{ detail.reporterName || detail.reporter || '-' }}</el-descriptions-item>
                <el-descriptions-item label="接收人">{{ detail.receiverName || detail.receiver || '-' }}</el-descriptions-item>
                <el-descriptions-item label="处理人">{{ detail.assigneeName || detail.assignee || '-' }}</el-descriptions-item>
                <el-descriptions-item label="完成时间">{{ detail.finishTime ? formatDateTime(detail.finishTime) : '-' }}</el-descriptions-item>
                <el-descriptions-item label="工单内容" :span="2">{{ detail.content || '-' }}</el-descriptions-item>
              </el-descriptions>

              <div class="timeline-section" v-if="timelineLogs.length">
                <div class="timeline-title">流程轨迹</div>
                <el-timeline>
                  <el-timeline-item
                    v-for="(log, idx) in timelineLogs"
                    :key="log.id || idx"
                    :type="timelineType(log.type)"
                  >
                    <div class="tl-header">
                      <span class="tl-label">{{ timelineLabel(log.type) }}</span>
                      <span class="tl-operator" v-if="log.operator">· {{ log.operator }}</span>
                      <span class="tl-time">{{ formatDateTime(log.create_time) }}</span>
                    </div>
                    <div class="tl-content" v-if="log.content">{{ log.content }}</div>
                    <div class="tl-media" v-if="parseLogMedia(log).length">
                      <template v-for="(m, mi) in parseLogMedia(log)" :key="mi">
                        <el-image v-if="m.type === 'image'" :src="m.url" :preview-src-list="[m.url]" fit="cover" class="media-thumb" />
                        <div v-else-if="m.type === 'video'" class="video-thumb" @click="openVideo(m)">
                          <el-icon :size="24"><VideoPlay /></el-icon>
                          <span style="font-size:11px">点击播放</span>
                        </div>
                        <div v-else-if="m.type === 'voice'" class="voice-play" @click="playVoice(m, 'log-' + log.id + '-' + mi)">
                          🎤 语音<template v-if="m.duration"> · {{ m.duration }}″</template>
                        </div>
                      </template>
                    </div>
                  </el-timeline-item>
                </el-timeline>
              </div>
              <div class="tl-empty" v-else>暂无流程记录</div>
            </template>
          </div>
        </div>
      </div>
    </transition>

    <!-- 视频播放弹窗 -->
    <el-dialog v-model="videoVisible" width="720px" align-center :show-close="false">
      <div class="video-play-wrap">
        <video v-if="currentVideo" :src="currentVideo.url" controls autoplay class="video-play" />
        <div class="video-play-close" @click="closeVideo">✕</div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Close, VideoPlay } from '@element-plus/icons-vue'
import { getWorkOrderListPage, saveWorkOrder, assignWorkOrder, deleteWorkOrder, getWorkOrderById, getGrainStoreList, getUserList } from '../api'
import { formatDateTime } from '../utils/format'
import MyPager from '../components/MyPager.vue'

const workOrders = ref([])
const dialogVisible = ref(false)
const pageNum = ref(1)
const pageSize = ref(20)
const total = ref(0)
const detailVisible = ref(false)
const detail = ref(null)
const timelineLogs = ref([])
const playingVoiceIdx = ref(-1)
const voiceAudioCtx = ref(null)
const videoVisible = ref(false)
const currentVideo = ref(null)
const grainStores = ref([])
const users = ref([])

// 派单弹窗
const assignVisible = ref(false)
const assignRow = ref(null)
const assignForm = reactive({ priority: 2, assigneeId: null })

const query = reactive({ orderNo: '', title: '', status: null })

const filteredWorkOrders = computed(() => {
  let list = workOrders.value
  if (query.status !== null && query.status !== '' && query.status !== undefined) {
    list = list.filter(o => o.status === Number(query.status))
  }
  const kw = (query.orderNo || '').trim().toLowerCase()
  if (kw) list = list.filter(o => o.orderNo?.toLowerCase().includes(kw))
  const kt = (query.title || '').trim().toLowerCase()
  if (kt) list = list.filter(o => o.title?.toLowerCase().includes(kt))
  return list
})

const formData = reactive({
  id: null, title: '', content: '', grainStoreId: null,
  reporterId: null, reporter: ''
})

onMounted(() => {
  loadWorkOrders()
  loadGrainStores()
  loadUsers()
})

const loadWorkOrders = async () => {
  try {
    const res = await getWorkOrderListPage({ page: pageNum.value, size: pageSize.value })
    workOrders.value = res.data?.list || []
    total.value = res.data?.total || 0
  } catch (error) {
    ElMessage.error('加载工单失败')
  }
}
const loadGrainStores = async () => {
  try {
    const res = await getGrainStoreList()
    grainStores.value = res.data || []
  } catch (error) { /* 静默 */ }
}
const loadUsers = async () => {
  try {
    const res = await getUserList()
    users.value = res.data || []
  } catch (error) { /* 静默 */ }
}

const openModal = () => {
  Object.assign(formData, {
    id: null, title: '', content: '', grainStoreId: null,
    reporterId: null, reporter: ''
  })
  dialogVisible.value = true
}

const handleSaveWorkOrder = async () => {
  try {
    await saveWorkOrder(formData)
    ElMessage.success('保存成功')
    dialogVisible.value = false
    loadWorkOrders()
  } catch (error) {
    ElMessage.error('保存失败')
  }
}

const openAssign = (row) => {
  assignRow.value = row
  Object.assign(assignForm, {
    priority: row.priority || 2,
    assigneeId: row.assigneeId || null
  })
  assignVisible.value = true
}

const handleAssign = async () => {
  if (!assignForm.assigneeId) { ElMessage.warning('请选择处理人'); return }
  try {
    const data = {
      assigneeId: assignForm.assigneeId,
      priority: assignForm.priority,
      status: 1  // 派单后自动变处理中
    }
    await assignWorkOrder(assignRow.value.id, data)
    ElMessage.success('派单成功')
    assignVisible.value = false
    loadWorkOrders()
  } catch (error) {
    ElMessage.error(error.message || '派单失败')
  }
}

const handleDeleteWorkOrder = async (id) => {
  try {
    await deleteWorkOrder(id)
    ElMessage.success('删除成功')
    loadWorkOrders()
  } catch (error) {
    ElMessage.error('删除失败')
  }
}

const resetQuery = () => {
  Object.assign(query, { orderNo: '', title: '', status: null })
  loadWorkOrders()
}

const viewDetail = async (row) => {
  // 先立即显示（用表格行的数据，保证不管 API 成功失败都有内容）
  detail.value = row
  timelineLogs.value = []
  detailVisible.value = true
  playingVoiceIdx.value = -1

  // 再异步尝试拿完整数据 + logs
  try {
    const res = await getWorkOrderById(row.id)
    const d = res.data
    if (d && d.workOrder) {
      detail.value = Object.assign({}, row, d.workOrder)
      timelineLogs.value = d.logs || []
    } else if (d && typeof d === 'object') {
      // 可能后端直接平铺返回 WorkOrder
      detail.value = Object.assign({}, row, d)
      timelineLogs.value = []
    }
  } catch (e) {
    // 接口失败时保留表格行数据
  }
}

// ===== 时间线辅助 =====
const timelineLabel = (t) => ({ created: '发起', assigned: '派单', processing: '处理中', completed: '已完成', rejected: '已驳回' }[t] || t)
const timelineType = (t) => ({ created: 'primary', assigned: 'warning', processing: 'success', completed: 'success', rejected: 'danger' }[t] || 'info')
const parseLogMedia = (log) => {
  if (!log?.media_urls) return []
  try { return JSON.parse(log.media_urls) } catch { return [] }
}

const playVoice = (m, idx) => {
  if (!voiceAudioCtx.value) voiceAudioCtx.value = new Audio()
  if (playingVoiceIdx.value >= 0 && playingVoiceIdx.value !== idx) {
    voiceAudioCtx.value.pause(); voiceAudioCtx.value.currentTime = 0
  }
  if (playingVoiceIdx.value === idx) { voiceAudioCtx.value.pause(); playingVoiceIdx.value = -1; return }
  voiceAudioCtx.value.src = m.url
  voiceAudioCtx.value.onended = () => { playingVoiceIdx.value = -1 }
  voiceAudioCtx.value.play()
  playingVoiceIdx.value = idx
}

const openVideo = (m) => { currentVideo.value = m; videoVisible.value = true }
const closeVideo = () => {
  videoVisible.value = false
  if (currentVideo.value) { const el = document.querySelector('.video-play'); if (el) el.pause?.() }
  currentVideo.value = null
}

const getPriorityName = (priority) => ({ 1: '紧急', 2: '普通', 3: '低' }[priority] || '普通')
const getPriorityType = (priority) => ({ 1: 'danger', 2: 'warning', 3: 'info' }[priority] || 'info')
const getStatusName = (status) => ({ 0: '待处理', 1: '处理中', 2: '已完成', 3: '已关闭' }[status] || '未知')
const getStatusType = (status) => ({ 0: 'warning', 1: 'primary', 2: 'success', 3: 'info' }[status] || 'info')
</script>

<style scoped>
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.actions, .filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.order-mask {
  position: fixed;
  left: 240px;
  top: 84px;
  right: 20px;
  bottom: 20px;
  background: rgba(0, 0, 0, 0.45);
  z-index: 999;
}
.order-panel {
  position: absolute;
  inset: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}
.order-title {
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  font-weight: 600;
  font-size: 15px;
  color: #303133;
  border-bottom: 1px solid #ebeef5;
}
.order-body { flex: 1; overflow-y: auto; padding: 16px; }

.media-thumb { width: 120px; height: 120px; border-radius: 4px; border: 1px solid #ebeef5; cursor: zoom-in; background: #f5f7fa; }
.video-thumb { width: 120px; height: 120px; border-radius: 4px; border: 1px solid #ebeef5; cursor: pointer; background: #1a1a1a; color: #fff; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; font-size: 12px; }
.video-thumb:hover { border-color: #07c160; color: #07c160; }
.voice-play { padding: 10px 16px; background: #f0f9eb; border: 1px solid #e1f3d8; border-radius: 6px; cursor: pointer; color: #67c23a; font-size: 13px; display: flex; align-items: center; gap: 8px; height: 40px; }
.voice-play:hover { background: #e1f3d8; }

.video-play-wrap { position: relative; text-align: center; }
.video-play { width: 100%; max-height: 70vh; border-radius: 4px; }
.video-play-close { position: absolute; top: -12px; right: -12px; width: 28px; height: 28px; border-radius: 50%; background: rgba(0,0,0,0.6); color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 16px; }

.timeline-section { margin-top: 20px; padding-top: 16px; border-top: 1px solid #ebeef5; }
.timeline-title { font-weight: 600; font-size: 14px; color: #303133; margin-bottom: 12px; }
.tl-header { display: flex; align-items: center; gap: 6px; font-size: 13px; margin-bottom: 4px; }
.tl-label { font-weight: 600; color: #303133; }
.tl-operator { color: #606266; }
.tl-time { color: #909399; font-size: 12px; margin-left: auto; }
.tl-content { color: #606266; font-size: 13px; line-height: 1.6; margin: 4px 0; white-space: pre-wrap; }
.tl-media { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
.tl-empty { margin-top: 20px; padding: 24px; text-align: center; color: #909399; font-size: 13px; background: #fafafa; border-radius: 4px; }

.manage-fade-enter-active, .manage-fade-leave-active { transition: opacity 0.2s ease; }
.manage-fade-enter-from, .manage-fade-leave-to { opacity: 0; }
</style>
